import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Stats {
  companies: number;
  users: number;
  projects: number;
  issues: number;
  tasks: number;
  openIssues: number;
  closedIssues: number;
}

interface Company {
  id: string;
  name: string;
  invite_code: string;
  created_at: string;
}

interface UserRow {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  company_id: string | null;
  created_at: string;
  companies?: { name: string } | null;
}

function StatCard({ value, label, icon, color }: { value: number | string; label: string; icon: React.ReactNode; color: string }) {
  return (
    <div className="sa-stat-card">
      <div className="sa-stat-icon" style={{ background: `${color}18`, color }}>{icon}</div>
      <div className="sa-stat-content">
        <div className="sa-stat-value">{typeof value === 'number' ? value.toLocaleString() : value}</div>
        <div className="sa-stat-label">{label}</div>
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {

  const [stats,      setStats]      = useState<Stats | null>(null);
  const [companies,  setCompanies]  = useState<Company[]>([]);
  const [users,      setUsers]      = useState<UserRow[]>([]);
  const [statsLoading,      setSL]  = useState(true);
  const [companiesLoading,  setCL]  = useState(true);
  const [usersLoading,      setUL]  = useState(true);
  const [error,      setError]      = useState('');
  const [userPage,   setUserPage]   = useState(1);
  const [userTotal,  setUserTotal]  = useState(0);
  const [compPage,   setCompPage]   = useState(1);
  const [compTotal,  setCompTotal]  = useState(0);
  const [roleUpdating, setRoleUpdating] = useState<string | null>(null);
  const [toast,      setToast]      = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const loadStats = useCallback(async () => {
    try {
      setSL(true);
      const data = await api.get<Stats>('/api/super-admin/stats');
      setStats(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSL(false);
    }
  }, []);

  const loadCompanies = useCallback(async (page: number) => {
    try {
      setCL(true);
      const data = await api.get<{ data: Company[]; total: number }>(`/api/super-admin/companies?page=${page}&limit=10`);
      setCompanies(data.data);
      setCompTotal(data.total);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCL(false);
    }
  }, []);

  const loadUsers = useCallback(async (page: number) => {
    try {
      setUL(true);
      const data = await api.get<{ data: UserRow[]; total: number }>(`/api/super-admin/users?page=${page}&limit=15`);
      setUsers(data.data);
      setUserTotal(data.total);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUL(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadCompanies(compPage); }, [loadCompanies, compPage]);
  useEffect(() => { loadUsers(userPage); }, [loadUsers, userPage]);

  const handleRoleChange = async (userId: string, role: string) => {
    setRoleUpdating(userId);
    try {
      await api.patch(`/api/super-admin/users/${userId}/role`, { role });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role } : u));
      showToast('Role updated successfully');
    } catch (e: any) {
      showToast(`Error: ${e.message}`);
    } finally {
      setRoleUpdating(null);
    }
  };

  const roleBadge = (role: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      admin:       { bg: '#ede9fe', color: '#6d28d9' },
      developer:   { bg: '#dbeafe', color: '#1d4ed8' },
      tester:      { bg: '#fef3c7', color: '#d97706' },
      super_admin: { bg: '#dcfce7', color: '#15803d' },
    };
    const style = map[role] || { bg: '#f3f4f6', color: '#374151' };
    return (
      <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: style.bg, color: style.color }}>
        {role}
      </span>
    );
  };

  const fmt = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  if (error) {
    return (
      <div className="page-container">
        <div className="error-msg" style={{ maxWidth: 500, margin: '80px auto', textAlign: 'center' }}>
          <strong>Access error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container sa-dashboard">
      {toast && <div className="sa-toast">{toast}</div>}

      {/* Header */}
      <div className="sa-header">
        <div>
          <h1 className="sa-title">Super Admin Dashboard</h1>
          <p className="sa-subtitle">System-wide overview and management console</p>
        </div>
        <button className="btn-secondary" onClick={() => { loadStats(); loadCompanies(compPage); loadUsers(userPage); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" style={{ marginRight: 6 }}>
            <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="sa-stats-grid">
        {statsLoading ? (
          Array.from({ length: 7 }).map((_, i) => <div key={i} className="sa-stat-skeleton" />)
        ) : stats ? (
          <>
            <StatCard value={stats.companies}    label="Companies"     color="#6366f1" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>} />
            <StatCard value={stats.users}        label="Total Users"   color="#1C9B82" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>} />
            <StatCard value={stats.projects}     label="Projects"      color="#3b82f6" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>} />
            <StatCard value={stats.issues}       label="Total Issues"  color="#f59e0b" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>} />
            <StatCard value={stats.openIssues}   label="Open Issues"   color="#ef4444" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22"><circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 14 14"/></svg>} />
            <StatCard value={stats.closedIssues} label="Closed Issues" color="#10b981" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>} />
            <StatCard value={stats.tasks}        label="Tasks"         color="#8b5cf6" icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>} />
          </>
        ) : null}
      </div>

      {/* Companies table */}
      <div className="sa-section">
        <div className="sa-section-header">
          <h2 className="sa-section-title">Companies</h2>
          <span className="sa-section-count">{compTotal} total</span>
        </div>
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Invite Code</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {companiesLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}><td colSpan={3}><div className="sa-row-skeleton" /></td></tr>
                ))
              ) : companies.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 32 }}>No companies yet</td></tr>
              ) : companies.map(c => (
                <tr key={c.id}>
                  <td><span className="sa-company-name">{c.name}</span></td>
                  <td><code className="sa-code">{c.invite_code}</code></td>
                  <td>{fmt(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {compTotal > 10 && (
          <div className="sa-pagination">
            <button className="btn-secondary sa-pg-btn" disabled={compPage === 1} onClick={() => setCompPage(p => p - 1)}>← Prev</button>
            <span className="sa-pg-info">Page {compPage} of {Math.ceil(compTotal / 10)}</span>
            <button className="btn-secondary sa-pg-btn" disabled={compPage >= Math.ceil(compTotal / 10)} onClick={() => setCompPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>

      {/* Users table */}
      <div className="sa-section">
        <div className="sa-section-header">
          <h2 className="sa-section-title">Users</h2>
          <span className="sa-section-count">{userTotal} total</span>
        </div>
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Name / Email</th>
                <th>Company</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Change Role</th>
              </tr>
            </thead>
            <tbody>
              {usersLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}><td colSpan={5}><div className="sa-row-skeleton" /></td></tr>
                ))
              ) : users.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 32 }}>No users yet</td></tr>
              ) : users.map(u => (
                <tr key={u.user_id}>
                  <td>
                    <div className="sa-user-name">{u.full_name || '—'}</div>
                    <div className="sa-user-email">{u.email}</div>
                  </td>
                  <td>{u.companies?.name || <span style={{ color: 'var(--color-text-muted)' }}>—</span>}</td>
                  <td>{roleBadge(u.role)}</td>
                  <td>{fmt(u.created_at)}</td>
                  <td>
                    {u.role !== 'super_admin' ? (
                      <select
                        className="sa-role-select"
                        value={u.role}
                        disabled={roleUpdating === u.user_id}
                        onChange={e => handleRoleChange(u.user_id, e.target.value)}
                      >
                        <option value="admin">admin</option>
                        <option value="developer">developer</option>
                        <option value="tester">tester</option>
                      </select>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Protected</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {userTotal > 15 && (
          <div className="sa-pagination">
            <button className="btn-secondary sa-pg-btn" disabled={userPage === 1} onClick={() => setUserPage(p => p - 1)}>← Prev</button>
            <span className="sa-pg-info">Page {userPage} of {Math.ceil(userTotal / 15)}</span>
            <button className="btn-secondary sa-pg-btn" disabled={userPage >= Math.ceil(userTotal / 15)} onClick={() => setUserPage(p => p + 1)}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
